import React, { useState, useEffect, useCallback } from 'react';

import { Plus } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import api from '../../../services/api';
import { fixMediaUrl } from '../../../utils/mediaHelpers';
import StoryCreator from './StoryCreator';
import StoryViewer from './StoryViewer';
import './StoryBar.css';


const StoryBar = ({ currentUser }) => {
  const { isDark } = useTheme();

  const [stories, setStories] = useState([]);
  const [myStories, setMyStories] = useState([]);
  const [showCreator, setShowCreator] = useState(false);
  const [showViewer, setShowViewer] = useState(false);
  const [selectedStoryIndex, setSelectedStoryIndex] = useState(0);
  const [selectedUserStories, setSelectedUserStories] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchStories = useCallback(async () => {

    try {
      setLoading(true);
      const response = await api.get('/stories');
      if (response.data.success) {
        // Grouper les stories par utilisateur
        const grouped = groupStoriesByUser(response.data.data);
        setStories(grouped);
      }
    } catch (error) {
      console.error('Error fetching stories:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMyStories = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      const response = await api.get(`/stories?user_id=${currentUser.id}`);
      if (response.data.success) {
        setMyStories(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching my stories:', error);
    }
  }, [currentUser?.id]);

  useEffect(() => {
    fetchStories();
    fetchMyStories();
  }, [fetchStories, fetchMyStories]);

  const groupStoriesByUser = (storiesList) => {

    const grouped = {};
    storiesList.forEach(story => {
      if (!grouped[story.user_id]) {
        grouped[story.user_id] = {
          user: story.user,
          stories: [],
        };
      }
      grouped[story.user_id].stories.push(story);
    });
    return Object.values(grouped);
  };

  const handleStoryClick = (userStories, index) => {
    setSelectedUserStories(userStories);
    setSelectedStoryIndex(index);
    setShowViewer(true);
  };

  const handleMyStoryClick = () => {
    if (myStories.length > 0) {
      setSelectedUserStories(myStories);
      setSelectedStoryIndex(0);
      setShowViewer(true);
    } else {
      setShowCreator(true);
    }
  };

  const handleStoryCreated = (newStory) => {
    setMyStories([...myStories, newStory]);
    fetchStories();
  };

  const getTimeLeft = (expiresAt) => {
    const expires = new Date(expiresAt);
    const now = new Date();
    const diff = expires - now;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    return hours > 0 ? `${hours}h` : 'Expiré';
  };

  const hasUnviewedStories = (userStories) => {
    // Vérifier si l'utilisateur a des stories non vues
    // Cette logique nécessiterait de stocker les vues localement ou via API
    return true; // Par défaut, montrer comme non vu
  };

  if (loading) {
    return (
      <div className={`story-bar ${isDark ? 'dark' : ''}`}>

        <div className="story-bar-loading">
          <div className="story-skeleton" />
          <div className="story-skeleton" />
          <div className="story-skeleton" />
          <div className="story-skeleton" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`story-bar ${isDark ? 'dark' : ''}`}>

        <div className="story-bar-scroll">
          {/* My Story */}
          <div 
            className="story-item my-story"
            onClick={handleMyStoryClick}
          >
            <div className={`story-ring ${myStories.length > 0 ? 'has-stories' : ''}`}>
              <img 

                src={fixMediaUrl(currentUser?.profile?.photo_url || currentUser?.profile?.profile_picture) || '/default-avatar.svg'} 
                alt="My story"
                className="story-avatar"
                onError={(e) => {
                  if (e.target.getAttribute('data-error-handled') === 'true') return;
                  e.target.setAttribute('data-error-handled', 'true');
                  console.log('My avatar failed:', e.target.src);
                  e.target.src = '/default-avatar.svg';
                }}


              />



              {myStories.length === 0 && (
                <div className="add-story-btn">
                  <Plus size={16} />
                </div>
              )}
            </div>
            <span className="story-username">
              {myStories.length > 0 ? 'Ma story' : 'Ajouter'}
            </span>
            {myStories.length > 0 && (
              <span className="story-time">{getTimeLeft(myStories[0].expires_at)}</span>
            )}
          </div>

          {/* Other users' stories */}
          {stories.map((userStoryGroup, index) => {
            // Ne pas afficher mes propres stories dans la liste principale
            if (userStoryGroup.user.id === currentUser?.id) return null;

            const firstStory = userStoryGroup.stories[0];
            const unviewed = hasUnviewedStories(userStoryGroup.stories);

            return (
              <div 
                key={userStoryGroup.user.id}
                className="story-item"
                onClick={() => handleStoryClick(userStoryGroup.stories, 0)}
              >
                <div className={`story-ring ${unviewed ? 'unviewed' : 'viewed'}`}>
                  <img 

                    src={fixMediaUrl(userStoryGroup.user.profile?.photo_url || userStoryGroup.user.profile?.profile_picture) || '/default-avatar.svg'} 
                    alt={userStoryGroup.user.first_name}
                    className="story-avatar"
                    onError={(e) => {
                      if (e.target.getAttribute('data-error-handled') === 'true') return;
                      e.target.setAttribute('data-error-handled', 'true');
                      console.log('Other avatar failed:', e.target.src);
                      e.target.src = '/default-avatar.svg';
                    }}


                  />


                </div>

                <span className="story-username">
                  {userStoryGroup.user.first_name}
                </span>
                <span className="story-time">
                  {getTimeLeft(firstStory.expires_at)}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modals */}
      {showCreator && (
        <StoryCreator 
          onClose={() => setShowCreator(false)}
          onStoryCreated={handleStoryCreated}
        />
      )}

      {showViewer && (
        <StoryViewer 
          stories={selectedUserStories}
          initialIndex={selectedStoryIndex}
          onClose={() => {
            // Defer state updates to avoid React warning
            setTimeout(() => {
              setShowViewer(false);
              fetchStories(); // Rafraîchir après visionnage
            }, 0);
          }}
          currentUser={currentUser}
        />
      )}

    </>
  );
};

export default StoryBar;
